import { DataTableDateCell, DataTableTableViewMode, type DataTableItem, type PartialDataTableConfig } from '@common';
import type { ParcelStatData, ZoneStatData } from '../models';
import { LayerGroupKind } from '../../geo-layers-upload/enums';
import { StatisticsKind } from '../enums';
import { markRaw } from 'vue';
import SoilTemperatureClassificationCell from '../components/statistics-details/SoilTemperatureClassificationCell.vue';
import YieldZoneStrengthDeltaCell from '../components/statistics-details/YieldZoneStrengthDeltaCell.vue';
import { StatisticsDataTableCropCell } from '../components';
import DecisionSupportYieldCell from '../components/statistics-details/DecisionSupportYieldCell.vue';
import SoilParameterStrengthCell from '../components/statistics-details/SoilParameterStrengthCell.vue';
import SoilParameterColumnHeaderCell from '../components/statistics-details/SoilParameterColumnHeaderCell.vue';

const STATISTICS_PARCEL_STAT_BASETABLE: PartialDataTableConfig = {
  needTableHeader: true,
  showViewModes: false,
  fixedHeader: true,
  showIndexColumn: false,
  viewModes: [
    {
      name: 'table',
      label: 'dataTable.viewMode.table',
      icon: 'mdi-view-list',
      translateLabel: true,
      showSortSelect: false,
      showColumnSelect: true,
      component: DataTableTableViewMode as any,
      componentConfig: {}
    }
  ],
  clickableItems: false,
  height: '100%',
  hover: true,
  columns: [
    {
      name: 'name',
      label: 'statistics.label.parcelName',
      translateLabel: true,
      sortable: true,
      class: 'col-default'
    },
    {
      name: 'area',
      label: 'statistics.label.area',
      translateLabel: true,
      cellConfig: {
        emptyValue: '-',
        postfix: ' ha'
      },
      class: 'col-default',
      sortable: true
    }
  ],
  actions: {
    global: [],
    row: [
      {
        action: 'zoomTo',
        actionConfig: {
          label: 'button.zoomToParcel',
          icon: 'mdi-magnify-plus-outline',
          variant: 'flat',
          translateLabel: true
        }
      }
    ]
  },
  reactiveClassCondition: (item: DataTableItem<ParcelStatData>) =>
    item.data.originalZones?.some((zone) => zone.selected) ? 'data-table-row--selected' : ''
};

const STATISTICS_ZONE_STAT_BASETABLE: PartialDataTableConfig = {
  needTableHeader: true,
  showViewModes: false,
  fixedHeader: true,
  showIndexColumn: false,
  viewModes: [
    {
      name: 'table',
      label: 'dataTable.viewMode.table',
      icon: 'mdi-view-list',
      translateLabel: true,
      showSortSelect: false,
      showColumnSelect: true,
      component: DataTableTableViewMode as any,
      componentConfig: {}
    }
  ],
  clickableItems: false,
  height: '100%',
  hover: true,
  columns: [
    {
      name: 'index',
      label: 'statistics.label.zoneNumber',
      translateLabel: true,
      sortable: true,
      class: 'col-zoneNumber'
    },
    {
      name: 'area',
      label: 'statistics.label.area',
      translateLabel: true,
      cellConfig: {
        emptyValue: '-',
        postfix: ' ha'
      },
      class: 'col-default',
      sortable: true
    }
  ],
  actions: {
    global: [],
    row: []
  },
  reactiveClassCondition: (item: DataTableItem<ZoneStatData>) =>
    item.data.originalZones?.some((zone) => zone.selected) ? 'data-table-row--selected' : ''
};

const statisticsCropColumns: PartialDataTableConfig['columns'] = [
  {
    name: 'crop',
    label: 'statistics.label.crop',
    translateLabel: true,
    sortable: true,
    cellComponent: markRaw(StatisticsDataTableCropCell),
    class: 'col-default'
  }
];

const statisticsYieldZoneStrengthColumns = [
  {
    name: 'yieldRank',
    label: 'statistics.label.yieldRank',
    translateLabel: true,
    sortable: true,
    class: 'col-default'
  },
  {
    name: 'zoneStrengthRank',
    label: 'statistics.label.zoneStrengthRank',
    translateLabel: true,
    sortable: true,
    class: 'col-default'
  },
  {
    name: 'delta',
    label: 'statistics.label.delta',
    translateLabel: true,
    sortable: true,
    class: 'col-default',
    cellComponent: markRaw(YieldZoneStrengthDeltaCell)
  },
  {
    name: 'yieldValue',
    label: 'statistics.label.statisticsYieldValue',
    translateLabel: true,
    sortable: true,
    cellConfig: {
      emptyValue: '-',
      format: 'decimal',
      postfix: ' t/ha'
    },
    class: 'col-default'
  },
  {
    name: 'zoneStrength',
    label: 'statistics.label.zoneStrength',
    translateLabel: true,
    sortable: true,
    cellConfig: {
      emptyValue: '-',
      format: 'decimal',
      postfix: ' %'
    },
    class: 'col-default'
  }
];

const statisticsYieldStemNumberColumns = [
  {
    name: 'yieldValue',
    label: 'statistics.label.realYieldValue',
    translateLabel: true,
    sortable: true,
    cellConfig: {
      postfix: ' t/ha'
    },
    class: 'col-default'
  },
  {
    name: 'stemNumber',
    label: 'statistics.label.stemNumber',
    translateLabel: true,
    sortable: true,
    class: 'col-default'
  },

  {
    name: 'yieldVsStemNumber',
    label: 'statistics.label.yieldVsStemNumber',
    translateLabel: true,
    sortable: true,
    class: 'col-default'
  }
];

const statisticsYieldAppliedDoseColumns = [
  {
    name: 'yieldValue',
    label: 'statistics.label.realYieldValue',
    translateLabel: true,
    sortable: true,
    cellConfig: {
      postfix: ' t/ha'
    },
    class: 'col-default'
  },
  {
    name: 'yieldVsK',
    label: 'statistics.label.yieldVsK',
    translateLabel: true,
    sortable: true,
    class: 'col-default',
    cellConfig: {
      postfix: ' %'
    }
  },
  {
    name: 'yieldVsP',
    label: 'statistics.label.yieldVsP',
    translateLabel: true,
    sortable: true,
    class: 'col-default',
    cellConfig: {
      postfix: ' %'
    }
  },
  {
    name: 'yieldVsN',
    label: 'statistics.label.yieldVsN',
    translateLabel: true,
    sortable: true,
    class: 'col-default',
    cellConfig: {
      postfix: ' %'
    }
  }
];

const statisticsSoilTemperatureColumns: PartialDataTableConfig['columns'] = [
  {
    name: 'lowestTemperature',
    label: 'statistics.label.lowestTemperature',
    translateLabel: true,
    sortable: true,
    class: 'col-default'
  },
  {
    name: 'lowestTemperatureDate',
    label: 'statistics.label.lowestTemperatureDate',
    translateLabel: true,
    sortable: true,
    cellConfig: {
      dateFormat: 'yyyy.MM.dd'
    },
    class: 'col-default',
    cellComponent: markRaw(DataTableDateCell)
  },
  {
    name: 'classification',
    label: 'statistics.label.classification',
    class: 'col-classification',
    translateLabel: true,
    sortable: true,
    cellComponent: markRaw(SoilTemperatureClassificationCell)
  }
];

const statisticsDecisionSupportColumns: PartialDataTableConfig['columns'] = [
  {
    name: 'yieldValue',
    label: 'statistics.label.yieldValue',
    translateLabel: true,
    sortable: true,
    cellComponent: markRaw(DecisionSupportYieldCell),
    class: 'col-default'
  },
  {
    name: 'estimatedRevenuePerHectare',
    label: 'statistics.label.estimatedRevenuePerHectare',
    translateLabel: true,
    sortable: true,
    cellConfig: {
      format: 'currency'
    },
    class: 'col-price col-highlighted'
  },
  {
    name: 'estimatedRevenue',
    label: 'statistics.label.estimatedRevenue',
    translateLabel: true,
    sortable: true,
    cellConfig: {
      format: 'currency'
    },
    class: 'col-price col-highlighted'
  },
  {
    name: 'estimatedIncomePerHectare',
    label: 'statistics.label.estimatedIncomePerHectare',
    translateLabel: true,
    sortable: true,
    cellConfig: {
      format: 'currency'
    },
    class: 'col-price'
  },
  {
    name: 'estimatedIncome',
    label: 'statistics.label.estimatedIncome',
    translateLabel: true,
    sortable: true,
    cellConfig: {
      format: 'currency'
    },
    class: 'col-price'
  },
  {
    name: 'upkeepCost',
    label: 'statistics.label.upkeepCost',
    translateLabel: true,
    sortable: true,
    cellConfig: {
      format: 'currency'
    },
    class: 'col-price'
  },
  {
    name: 'cropPrice',
    label: 'statistics.label.cropPrice',
    translateLabel: true,
    sortable: true,
    cellConfig: {
      format: 'currency',
      postfix: '/t'
    },
    class: 'col-price'
  }
];

const specificZoneStatColumns: Partial<Record<LayerGroupKind, PartialDataTableConfig['columns']>> = {
  [LayerGroupKind.STATISTICS_YIELD_ZONE_STRENGTH]: statisticsYieldZoneStrengthColumns,
  [LayerGroupKind.STATISTICS_YIELD_STEM_NUMBER]: statisticsYieldStemNumberColumns,
  [LayerGroupKind.STATISTICS_YIELD_APPLIED_DOSE]: statisticsYieldAppliedDoseColumns,
  [LayerGroupKind.STATISTICS_DECISION_SUPPORT]: statisticsDecisionSupportColumns
};

const specificParcelStatColumns: Partial<Record<StatisticsKind, PartialDataTableConfig['columns']>> = {
  [StatisticsKind.YIELD_STEM_NUMBER]: statisticsYieldStemNumberColumns,
  [StatisticsKind.SOIL_TEMPERATURE]: statisticsSoilTemperatureColumns,
  [StatisticsKind.YIELD_APPLIED_DOSE]: statisticsYieldAppliedDoseColumns,
  [StatisticsKind.DECISION_SUPPORT]: statisticsDecisionSupportColumns
};

export const getStatisticsZoneStatTableConfig = (layerGroupKind: LayerGroupKind) => {
  const statTable = { ...STATISTICS_ZONE_STAT_BASETABLE, columns: [...(STATISTICS_ZONE_STAT_BASETABLE.columns || [])] };

  statTable.columns.push(...(specificZoneStatColumns[layerGroupKind] || []));
  return statTable;
};

const STATISTICS_SOIL_PARAMETER_TABLE: PartialDataTableConfig = {
  needTableHeader: true,
  showViewModes: false,
  fixedHeader: true,
  showIndexColumn: false,
  viewModes: [
    {
      name: 'table',
      label: 'dataTable.viewMode.table',
      icon: 'mdi-view-list',
      translateLabel: true,
      showSortSelect: false,
      showColumnSelect: true,
      component: DataTableTableViewMode as any,
      componentConfig: {}
    }
  ],
  clickableItems: false,
  height: '100%',
  hover: true,
  columns: [
    {
      name: 'name',
      label: 'statistics.label.soilParameterName',
      translateLabel: true,
      class: 'col-soilParameter'
    },
    {
      name: 'stdBeta',
      label: 'statistics.label.stdBeta',
      translateLabel: true,
      class: 'col-default',
      cellHeaderComponent: markRaw(SoilParameterColumnHeaderCell)
    },
    {
      name: 'r2',
      label: 'statistics.label.r2',
      translateLabel: true,
      class: 'col-default',
      cellHeaderComponent: markRaw(SoilParameterColumnHeaderCell)
    },
    {
      name: 'pValue',
      label: 'statistics.label.pValue',
      translateLabel: true,
      class: 'col-default',
      cellHeaderComponent: markRaw(SoilParameterColumnHeaderCell)
    },
    {
      name: 'n',
      label: 'statistics.label.n',
      translateLabel: true,
      class: 'col-default',
      cellHeaderComponent: markRaw(SoilParameterColumnHeaderCell)
    },
    {
      name: 'strength',
      label: 'statistics.label.strength',
      translateLabel: true,
      class: 'col-default',
      cellHeaderComponent: markRaw(SoilParameterColumnHeaderCell),
      cellComponent: markRaw(SoilParameterStrengthCell)
    }
  ],
  actions: {
    global: [],
    row: []
  }
};

export const getStatisticsSoilParameterTableConfig = (): PartialDataTableConfig => ({
  ...STATISTICS_SOIL_PARAMETER_TABLE,
  columns: [...(STATISTICS_SOIL_PARAMETER_TABLE.columns || [])]
});

export const getStatisticsParcelStatTableConfig = (statisticsKind: StatisticsKind) => {
  const statTable = {
    ...STATISTICS_PARCEL_STAT_BASETABLE,
    columns: [
      ...(STATISTICS_PARCEL_STAT_BASETABLE.columns || []),
      ...(statisticsKind === StatisticsKind.DECISION_SUPPORT ? [] : statisticsCropColumns)
    ]
  };

  statTable.columns.push(...(specificParcelStatColumns[statisticsKind] || []));
  return statTable;
};
